/**
 * "Is this symbol streaming?" — the colour vs grey state of a symbol icon.
 *
 * The rule is deliberately simple: a symbol is live while its price has moved within the last
 * two minutes. No session calendar, no gateway involvement.
 */

import { useSyncExternalStore } from "react";
import { useSubscribedSymbolsStore } from "@/vybe-trader-client/subscribed-symbols/stores/useSubscribedSymbolsStore";

export const SYMBOL_LIVE_WINDOW_MS = 120_000;
const CLOCK_STEP_MS = 10_000;

export function isSymbolLive(lastMoveAt: number | undefined, now: number): boolean {
  return lastMoveAt !== undefined && now - lastMoveAt < SYMBOL_LIVE_WINDOW_MS;
}

// One shared timer for every icon on screen; it only runs while something is subscribed.
let clock = 0;
let timer: ReturnType<typeof setInterval> | null = null;
const listeners = new Set<() => void>();

function subscribeClock(listener: () => void): () => void {
  listeners.add(listener);
  if (!timer) {
    timer = setInterval(() => {
      clock += 1;
      listeners.forEach((l) => l());
    }, CLOCK_STEP_MS);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && timer) {
      clearInterval(timer);
      timer = null;
    }
  };
}

const getClock = () => clock;

/**
 * True while the watchlisted symbol is ticking. The store selector yields a boolean, so a
 * tick re-renders the caller only when the state flips; the clock catches the flip to grey,
 * which by definition arrives with no tick.
 */
export function useSymbolLive(currency: string): boolean {
  useSyncExternalStore(subscribeClock, getClock, getClock);
  return useSubscribedSymbolsStore((state) =>
    isSymbolLive(state.byCurrency.get(currency)?.lastMoveAt, Date.now()),
  );
}
